import React, { useEffect, useState, useMemo } from 'react';
import LayoutRightSidebar from '@/layouts/LayoutRightSidebar';
import DInputBlock from '@/components/Global/DInput/DInputBlock';
import DInput from '@/components/Global/DInput/DInput';
import DTextArea from '@/components/Global/DInput/DTextArea';
import Checkbox from '@/shared/components/UI/Checkbox';
import InfoIcon from '@/shared/icons/InfoIcon';
import DTooltip from '@/components/Global/DTooltip';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import Input from '@/shared/components/UI/Input';
import Textarea from '@/shared/components/UI/Textarea';
import './index.css';
import BubblePreviewWrapper from '@/components/BubblePreviewWrapper';
import useCustomizationData from '@/hooks/useCustomization';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUserStore } from '@/stores/user/userStore';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import LLMSelector from '@/components/LLMSelector';
import LLMSelectorSimple from '@/v3-dashboard/components/UI/LLMSelectorSimple';
import useLayoutStore from '@/stores/layout/layoutStore';
import CheckIcon from '@/shared/icons/CheckIcon';
import Button from '@/shared/components/UI/Button';
import PageHeader from '@/v3-dashboard/components/UI/PageHeader';
import PageContainer from '@/v3-dashboard/components/UI/PageContainer';
import ArrowLeft from '@/shared/icons/ArrowLeft';
import useDanteApi from '@/hooks/useDanteApi';
import * as chatbotService from '@/services/chatbot.service';
import useToast from '@/hooks/useToast';
import DLoading from '@/components/DLoading';
import DConfirmationModal from '@/components/Global/DConfirmationModal';
import useIsAboveBreakpoint from '@/helpers/useIsAboveBreakpoint';
import clsx from 'clsx';
import LLMSelectorMobile from '@/v3-dashboard/components/UI/LLMSelectorMobile';
import loadIcon from '@/helpers/loadIcons';
import OpenAiLogoIcon from '@/shared/icons/OpenAiLogoIcon';
import { groupBy } from 'lodash';
import useLLMModels from '@/hooks/useLLMModels';
import mixpanel from '@/utils/mixpanel';
import ChevronLeftIcon from '@/shared/icons/ChevronLeftIcon';
import useChatbotOnboardingActions from '@/hooks/useChatbotOnboardingActions';
import { useJoyrideStore } from '@/stores/joyride/joyrideStore';
import useJoyrideTour from '@/hooks/useJoyrideTour';

const DEFAULT_BEHAVIOR_INSTRUCTIONS = `You are a helpful and professional AI assistant. Respond in a friendly, clear, and concise manner. Always prioritize accuracy and relevance based on the knowledge base provided to you. If you're unsure about something, let the user know rather than guessing. Keep your responses well-structured and easy to understand.`;

const PERSONALITY_FORM_TOUR_STEPS = [
    {
        target: '[data-tour-id="personality-name"]',
        title: 'Name Your Personality',
        content: 'Give your personality a name that\'s easy to recognize, especially if you create more than one.',
        disableBeacon: true,
        tourKey: 'personality-form',
        spotlightClicks: true,
        data: {
            requireInput: true,
            inputSelector: '[data-tour-id="personality-name"] input',
        },
        showSkipButton: false,
        showProgress: false,
    },
    {
        target: '[data-tour-id="personality-description"]',
        title: 'Describe Your Personality',
        content: 'Add a short description so you remember what this personality is for. This is optional.',
        tourKey: 'personality-form',
        spotlightClicks: true,
        showSkipButton: false,
        showProgress: false,
    },
    {
        target: '[data-tour-id="personality-behavior"]',
        title: 'Set Behavior Instructions',
        content: "We've added a starting point. These instructions define how your AI agent responds: its tone, style, and rules. Customize them or move on.",
        tourKey: 'personality-form',
        spotlightClicks: true,
        showSkipButton: false,
        showProgress: false,
    },
    {
        target: '[data-tour-id="personality-creativity"]',
        title: 'Creativity Level',
        content: 'Adjust how varied your AI\'s responses are. Start in the middle and fine-tune based on how your customers respond.',
        tourKey: 'personality-form',
        spotlightClicks: true,
        showSkipButton: false,
        showProgress: false,
    },
    {
        target: '[data-tour-id="personality-llm"]',
        title: 'Choose Your Model',
        content: 'Pick a language model to test with. Start with GPT-4o Mini for quick, budget-friendly testing. You can switch to a premium model later.',
        tourKey: 'personality-form',
        spotlightClicks: true,
        showSkipButton: false,
        showProgress: false,
        placement: 'left',
    },
    {
        target: '[data-tour-id="personality-preview"]',
        title: 'Live Preview',
        content: 'Send a message to test your AI with the current settings. This is how it will respond to your customers.',
        tourKey: 'personality-form',
        spotlightClicks: true,
        showSkipButton: false,
        showProgress: false,
    },
    {
        target: '[data-tour-id="personality-create-profile"]',
        title: "You're All Set",
        content: 'Click "Create Personality" to save your settings. You can always come back to edit it later.',
        tourKey: 'personality-form',
        spotlightClicks: true,
        locale: { last: 'Got it' },
        showSkipButton: false,
        showProgress: false,
    },
];

const AddPersonality = () => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { auth } = useUserStore((state) => state);
    const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
    const setSidebarOpen = useLayoutStore((state) => state.setSidebarOpen);
    const { addSuccessToast, addErrorToast } = useToast();
    const { completeCurrentStep, currentStepId } = useChatbotOnboardingActions(params.id);
    const markTourCompleted = useJoyrideStore((state) => state.markTourCompleted);
    const stopTour = useJoyrideStore((state) => state.stopTour);

    const isEditMode = !!params.personalityId;
    const personalityId = params.personalityId;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [behaviorInstructions, setBehaviorInstructions] = useState('');
    const [creativityLevel, setCreativityLevel] = useState(50);
    const [enableGeneralKnowledge, setEnableGeneralKnowledge] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [personalityName, setPersonalityName] = useState('');
    const [nameError, setNameError] = useState('');
    const [behaviorInstructionsError, setBehaviorInstructionsError] = useState('');
    const [deleteConfirmModal, setDeleteConfirmModal] = useState({
        open: false,
        personalityId: null,
        personalityName: ''
    });
    const [deletePersonalityLoading, setDeletePersonalityLoading] = useState(false);
    const [showLLMSelector, setShowLLMSelector] = useState(false);
    const [icons, setIcons] = useState({});
    const [iconsLoaded, setIconsLoaded] = useState(false);

    const { customizationData } = useCustomizationData(true, params.id);
    const isMobile = useIsAboveBreakpoint('sm');
    const models = useLLMModels();

    // Fetch personality data when in edit mode
    const { data: personalityData, isLoading: isLoadingPersonality } = useDanteApi(
        chatbotService.getPersonalityById,
        [personalityId, params.id],
        { skip: !isEditMode || !personalityId || !params.id },
        personalityId,
        params.id
    );

    // Load LLM icons
    useEffect(() => {
        const loadModels = async () => {
            const groupedModels = groupBy(models, 'Logo');
            const iconComponents = {};

            for (const company of Object.keys(groupedModels)) {
                try {
                    const iconCompany = await loadIcon(company);
                    iconComponents[company] = iconCompany;
                } catch (e) {
                    console.error(`Failed to load icon ${company}`, e);
                }
            }

            setIcons(iconComponents);
            setIconsLoaded(true);
        };
        if (models?.length) {
            loadModels();
        }
    }, [models]);

    // Get icon for selected LLM
    const getSelectedLLMIcon = () => {
        const selectedModel = selectedChatbot?.last_model_used || selectedChatbot?.llmModel;
        if (!selectedModel?.Logo) return OpenAiLogoIcon;
        return icons[selectedModel.Logo] || OpenAiLogoIcon;
    };

    const handleCreativityChange = (e) => {
        setCreativityLevel(parseInt(e.target.value, 10));
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        if (nameError) {
            setNameError('');
        }
    };

    const handleBehaviorInstructionsChange = (e) => {
        setBehaviorInstructions(e.target.value);
        if (behaviorInstructionsError) {
            setBehaviorInstructionsError('');
        }
    };

    const handleGetHelpClick = () => {
        window.parent.postMessage({
            eventType: 'tooltipPromptClick',
            eventData: 'I need help with behavior instructions'
        }, '*');
    };

    const handleLLMHelpClick = () => {
        window.parent.postMessage({
            eventType: 'tooltipPromptClick',
            eventData: 'I need help with selecting an LLM'
        }, '*');
    };

    // Track original values for dirty-state detection
    const [originalValues, setOriginalValues] = useState(null);

    useEffect(() => {
        if (isEditMode && personalityData) {
            const personality = personalityData;
            const values = {
                name: personality.personality_name || '',
                description: personality.personality_explanation || '',
                behaviorInstructions: personality.personality_content || '',
                creativityLevel: personality.creativity_level != null ? personality.creativity_level * 100 : 0,
                enableGeneralKnowledge: personality.open_to_internet || false,
            };
            setName(values.name);
            setPersonalityName(values.name);
            setDescription(values.description);
            setBehaviorInstructions(values.behaviorInstructions);
            setCreativityLevel(values.creativityLevel);
            setEnableGeneralKnowledge(values.enableGeneralKnowledge);
            setOriginalValues(values);
        }
    }, [isEditMode, personalityData]);

    const hasChanges = useMemo(() => {
        if (!isEditMode) return true;
        if (!originalValues) return false;
        return (
            name !== originalValues.name ||
            description !== originalValues.description ||
            behaviorInstructions !== originalValues.behaviorInstructions ||
            creativityLevel !== originalValues.creativityLevel ||
            enableGeneralKnowledge !== originalValues.enableGeneralKnowledge
        );
    }, [isEditMode, originalValues, name, description, behaviorInstructions, creativityLevel, enableGeneralKnowledge]);

    const breadcrumbItems = useMemo(() => [
        { label: 'Personalities', path: `/chatbot/${params.id}/personality` },
        { label: '' },
    ], [params.id, isEditMode, personalityName, name]);

    const mainTitle = useMemo(() => {
        if (!isEditMode) {
            return 'Create Personality';
        }
        // In edit mode, wait for data to load before showing the name
        if (isLoadingPersonality || !personalityData) {
            return 'Edit Profile'; // Show fallback while loading
        }
        // Once data is loaded, use the data directly or the state (which should be synced via useEffect)
        return personalityData?.personality_name || personalityName || name || 'Edit Profile';
    }, [isEditMode, isLoadingPersonality, personalityData, personalityName, name]);

    useEffect(() => {
        setSidebarOpen(false);
    }, []);

    useEffect(() => {
        if (!isEditMode) {
            mixpanel.track('Add Personality Page Viewed', {
                chatbot_id: params.id,
            });
        }
    }, []);

    // Start personality form tour only when coming from the Behavior page tour
    const fromTour = location.state?.fromTour;
    useJoyrideTour({
        key: 'personality-form',
        steps: PERSONALITY_FORM_TOUR_STEPS,
        chatbotId: params.id,
        scrollContainerSelector: '[data-joyride-scroll-container]',
        enabled: !isEditMode && !!fromTour && currentStepId === 'define_personality',
    });

    // Pre-fill behavior instructions when tour starts
    useEffect(() => {
        if (!isEditMode && fromTour && !behaviorInstructions) {
            setBehaviorInstructions(DEFAULT_BEHAVIOR_INSTRUCTIONS);
        }
    }, [isEditMode, fromTour]);

    const handleSave = async () => {
        // Clear previous errors
        setNameError('');
        setBehaviorInstructionsError('');

        // Validate fields
        let hasErrors = false;

        if (!name.trim()) {
            setNameError('Name is required');
            hasErrors = true;
        }

        if (!behaviorInstructions.trim()) {
            setBehaviorInstructionsError('Behavior Instructions is required');
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        try {
            setIsLoading(true);

            if (isEditMode) {
                // Update existing personality
                await chatbotService.updatePersonalities(
                    personalityId,
                    behaviorInstructions,
                    name,
                    (creativityLevel / 100).toFixed(2),
                    enableGeneralKnowledge,
                    description
                );
                mixpanel.track('Personality Updated', {
                    chatbot_id: params.id,
                    personality_id: personalityId,
                });
                if (!currentStepId) {
                    addSuccessToast({
                        message: 'Personality updated successfully'
                    });
                }
            } else {
                // Create new personality
                await chatbotService.createPersonalities(
                    params.id,
                    name,
                    behaviorInstructions,
                    (creativityLevel / 100).toFixed(2),
                    enableGeneralKnowledge,
                    description
                );
                mixpanel.track('Personality Created', {
                    chatbot_id: params.id,
                    has_custom_instructions: behaviorInstructions.trim().length > 0,
                });
                if (!currentStepId) {
                    addSuccessToast({
                        message: 'Personality created successfully'
                    });
                }
            }

            // Update original values so button disables again after save
            setOriginalValues({
                name,
                description,
                behaviorInstructions,
                creativityLevel,
                enableGeneralKnowledge,
            });

            if (currentStepId === 'define_personality') {
                completeCurrentStep();
            }

            // Complete the personality form tour if active
            markTourCompleted('personality-form');
            stopTour();

            // Navigate back to personalities list
            // navigate(`/chatbot/${params.id}/personality`);
        } catch (error) {
            console.error('Failed to save personality:', error);
            addErrorToast({
                message: error?.response?.data?.detail || error?.message || `Failed to ${isEditMode ? 'update' : 'create'} personality`
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeletePersonality = async (personalityId) => {
        try {
            setDeletePersonalityLoading(true);
            await chatbotService.deletePersonalities(personalityId);

            mixpanel.track('Personality Deleted', {
                chatbot_id: params.id,
                personality_id: personalityId,
            });
            addSuccessToast({
                message: 'Personality deleted successfully'
            });

            setDeleteConfirmModal({ open: false, personalityId: null, personalityName: '' });

            // Navigate back to personalities list
            navigate(`/chatbot/${params.id}/personality`);
        } catch (error) {
            console.error('Failed to delete personality:', error);
            addErrorToast({
                message: error?.response?.data?.detail || error?.message || 'Failed to delete personality'
            });
        } finally {
            setDeletePersonalityLoading(false);
        }
    };

    // Show loading state for edit mode
    if (isEditMode && isLoadingPersonality) {
        return <DLoading show={true} height="h-screen" />;
    }

    return (
        <PageContainer>
            <LayoutRightSidebar
                  fitSidebarToViewport={true}
                  mainClassName="overflow-y-auto no-scrollbar"
                  previewButtonLabel="Preview"
                  RightSidebar={() => {
                    return (
                      <div className="w-full flex flex-col h-full min-h-0 bubble-preview-min-height:justify-center bubble-preview-min-height:preview:h-auto sm:gap-v3-sm">
                        {isMobile ? (
                          <div data-tour-id="personality-llm" className={clsx(
                            'flex flex-col gap-v3-xs w-full',
                            'flex flex-shrink-0 '
                          )}>
                            <p className="text-v3-black-max text-v3-h10 font-[510]">Testing with</p>
                            <div className="flex items-center self-stretch w-full ">
                              <LLMSelectorSimple helperText={<>
                                Not sure which LLM to choose?{' '}<button onClick={handleLLMHelpClick} className="text-v3-purple-primary hover:text-v3-purple-medium">Get Help</button> from the AI assistant or connect with a team member.
                                </>} selectedColor="bg-v3-black-placeholder" shouldHaveDropdownWidth={false} listButtonClass="border !border-v3-black-inactive bg-v3-dropdown" />
                            </div>
                          </div>
                        ) : (
                          <div data-tour-id="personality-llm" className="flex flex-col gap-v3-xs w-full flex-shrink-0">
                            <button
                              onClick={() => setShowLLMSelector(!showLLMSelector)}
                              className="flex items-center gap-v3-xs px-v3-sm py-v3-xs h-[48px]"
                            >

                              {iconsLoaded && (
                                <IconWrapper size={20}>
                                  {getSelectedLLMIcon() && React.createElement(getSelectedLLMIcon())}
                                </IconWrapper>
                              )}
                              <p className="text-v3-black-max text-v3-h10 font-[510]">Testing with</p>
                              <span className="text-v3-black-max text-v3-h11 font-medium">
                                {selectedChatbot?.last_model_used?.label || selectedChatbot?.llmModel?.label || 'Select Model'}
                              </span>
                            </button>
                            {showLLMSelector && (
                              <div className="fixed inset-0 z-50 bg-white flex flex-col">
                                {/* Back to Editing Header */}
                                <div
                                  className="
                                    group flex items-center gap-v3-xs flex-shrink-0
                                    h-v3-xxl
                                    px-v3-sm py-v3-xs
                                    cursor-pointer
                                    border-b border-v3-card-border
                                  "
                                  onClick={() => setShowLLMSelector(false)}
                                >
                                  <button
                                    onClick={() => setShowLLMSelector(false)}
                                    className="flex items-center group-hover:opacity-80 transition-opacity"
                                  >
                                    <IconWrapper size={24}>
                                      <ChevronLeftIcon className="text-v3-purple-primary group-hover:text-v3-purple-medium" />
                                    </IconWrapper>
                                  </button>
                                  <span className="text-v3-purple-primary group-hover:text-v3-purple-medium text-v3-h10 font-medium transition-colors">Back to Editing</span>
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto">
                                  <LLMSelectorMobile onClose={() => setShowLLMSelector(false)} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div data-tour-id="personality-preview" className="flex-1 h-full flex flex-col min-h-0 w-full sm:max-w-[424px] bubble-preview-min-height:preview:flex-none bubble-preview-min-height:preview:h-auto">
                          <BubblePreviewWrapper
                            showLauncher={false}
                            config={{
                              ...customizationData,
                              access_token: auth?.access_token,
                              home_tab_enabled: false,
                              remove_watermark: true,
                              disclaimer_enabled: true,
                              disclaimer_text: 'All conversations within the dashboard consume credits.',
                              behaviour_instructions: behaviorInstructions,
                              llmModel: selectedChatbot?.llmModel?.value || selectedChatbot?.last_model_used?.value,
                              creativity_level: (creativityLevel / 100).toFixed(2),
                              open_to_internet_knowledge: enableGeneralKnowledge,
                            }}
                            customizationData={customizationData}
                            bubbleProps={{
                              type: "chatbot",
                              chatbotName: customizationData?.display_name || customizationData?.name || 'Dante AI',
                              initialShouldFetchCustomization: false,
                              isInApp: true,
                              isPreviewMode: false,
                              rightSidebar: true,
                            }}
                          applyBannerOffset={false}
                          />
                        </div>
                      </div>
                    );
                  }}
            >
                <div className="flex flex-col items-start w-full pb-v3-xxhuge">
                    {/* Header Section */}
                    <div className="flex flex-col justify-center items-start self-stretch">
                        <PageHeader
                            title={mainTitle}
                            breadcrumbItems={breadcrumbItems}
                            titleIcon={<ArrowLeft className='text-white ' />}
                            titleOnClick={() => navigate(`/chatbot/${params.id}/personality`)}
                            titleClassName='pt-0'
                        />
                    </div>

                    {/* First Section - Name, Description, Behavior Instructions */}
                    <div className="flex py-v3-sm pb-v3-md flex-col items-end gap-v3-sm self-stretch">
                        <div data-tour-id="personality-name" className="w-full">
                            <Input
                                label="Name"
                                value={name}
                                onChange={handleNameChange}
                                placeholder="Enter personality name"
                                error={nameError}
                                helperText='Internal label for this personality'
                                disabled={isEditMode && isLoadingPersonality}
                                wrapperClassName="w-full"
                            />
                        </div>

                        <div data-tour-id="personality-description" className="w-full">
                            <Input
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter personality description"
                                disabled={isEditMode && isLoadingPersonality}
                                wrapperClassName="w-full"
                                helperText='Brief summary for your team'
                            />
                        </div>

                        <div data-tour-id="personality-behavior" className="w-full">
                            <Textarea
                                label="Behavior Instructions"
                                value={behaviorInstructions}
                                onChange={handleBehaviorInstructionsChange}
                                placeholder="Write a detailed prompt outlining your desired AI Agent behavior..."
                                rows={13}
                                error={behaviorInstructionsError}
                                disabled={isEditMode && isLoadingPersonality}
                                wrapperClassName="w-full"
                                helperText={
                                    <>
                                    Instructions for how the agent responds. <button onClick={handleGetHelpClick} className="text-v3-purple-primary hover:text-v3-purple-medium">Get Help</button> from the AI assistant or connect with a team member.
                                </>}
                            />
                        </div>
                    </div>

                    {/* Second Section - Creativity Level */}
                    <div data-tour-id="personality-creativity" className="flex py-v3-sm pb-v3-md flex-col items-start gap-v3-lg self-stretch">
                        {/* Creativity Level Container */}
                        <div className="flex flex-col items-start gap-v3-sm self-stretch">
                            {/* Label */}
                            <label className="!text-v3-black-max text-v3-h11 font-medium">
                                Creativity Level
                            </label>

                            {/* Input Range Container */}
                            <div className="flex flex-col items-start gap-v3-sm self-stretch">
                                {/* Range Input */}
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={creativityLevel }
                                    onChange={handleCreativityChange}
                                    disabled={isEditMode && isLoadingPersonality}
                                    className="creativity-range-input w-full"
                                    style={{
                                        background: `linear-gradient(to right, var(--v3-purple-primary) ${creativityLevel}%, var(--v3-card-border) ${creativityLevel}%)`,
                                    }}
                                />

                                {/* Labels with Info Icons */}
                                <div className="flex justify-between items-center self-stretch">
                                    {/* Left: More Consistent */}
                                    <div className="flex items-center gap-v3-xs h-5">
                                        <span className="text-v3-black-max font-inter-variable text-xs font-medium leading-4">
                                            More Consistent
                                        </span>
                                        <DTooltip
                                            content="Choose 'More Consistent' for similar responses to repeat questions."
                                            position="right"
                                            className="size-5"
                                        >
                                            <IconWrapper size={20}>
                                                <InfoIcon className="text-v3-blue-primary" />
                                            </IconWrapper>
                                        </DTooltip>
                                    </div>

                                    {/* Right: More Varied */}
                                    <div className="flex items-center gap-v3-xs h-5">
                                        <span className="text-v3-black-max font-inter-variable text-xs font-medium leading-4">
                                            More Varied
                                        </span>
                                        <DTooltip
                                            content="Choose 'More Varied' for dynamic responses to repeat questions."
                                            position="right"
                                            className="size-5"
                                        >
                                            <IconWrapper size={20}>
                                                <InfoIcon className="text-v3-blue-primary" />
                                            </IconWrapper>
                                        </DTooltip>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Checkbox: Enable general knowledge fallback */}
                        <div className="flex items-start gap-v3-xs">
                            <Checkbox
                                checked={enableGeneralKnowledge}
                                onChange={(checked) => setEnableGeneralKnowledge(checked)}
                                label="Enable general knowledge fallback"
                                disabled={isEditMode && isLoadingPersonality}
                                className="add-personality-checkbox"
                            />
                            <DTooltip
                                content={
                                    <div className="flex flex-col gap-v3-xs">
                                        <p className="text-sm">When enabled, your AI Agent will use its full general knowledge (from the language model) to answer questions-not just the files or links you uploaded.</p>
                                        <p className="text-sm">This means it won't be limited to your specific content (memory), and may generate broader or less controlled responses.</p>
                                        <p className="text-sm">Turn this off to keep your agent focused only on the content you've provided.</p>
                                    </div>
                                }
                                position="right"
                            >
                                <IconWrapper size={20}>
                                    <InfoIcon className="text-v3-blue-primary" />
                                </IconWrapper>
                            </DTooltip>
                        </div>
                    </div>

                    <div className='flex py-v3-sm justify-between items-center w-full'>
                        {isEditMode && personalityId ? (
                            <Button
                                label="Delete"
                                onClick={() => {
                                    setDeleteConfirmModal({
                                        open: true,
                                        personalityId: personalityId,
                                        personalityName: personalityName || name || 'this personality'
                                    });
                                }}
                                variant="danger-secondary"
                                className='!py-[10px]'
                                disabled={isLoading || deletePersonalityLoading}
                                size='lg'
                            />
                        ) : (
                            <div></div>
                        )}
                        <div data-tour-id="personality-create-profile">
                            <Button
                                label={isEditMode ? 'Save & Publish' : 'Create Personality'}
                                onClick={handleSave}
                                variant="primary"
                                className='!py-[10px]'
                                loading={isLoading}
                                loadingLabel='Updating...'
                                disabled={isLoading || (isEditMode && isLoadingPersonality) || !hasChanges}
                                size='lg'
                            />
                        </div>
                    </div>

                    {/* Delete Confirmation Modal */}
                    <DConfirmationModal
                        open={deleteConfirmModal.open}
                        onClose={() => setDeleteConfirmModal({ open: false, personalityId: null, personalityName: '' })}
                        onConfirm={() => handleDeletePersonality(deleteConfirmModal.personalityId)}
                        title="Delete Personality"
                        description={`Are you sure you want to delete "${deleteConfirmModal.personalityName}"? This action cannot be undone.`}
                        confirmText="Delete"
                        cancelText="Cancel"
                        variantConfirm="danger"
                    loading={deletePersonalityLoading}
                />
            </div>
            </LayoutRightSidebar>
        </PageContainer>
    );
};

export default AddPersonality;
